/**
 * The eight demonstrations (SPEC_CAPTURE_UX_R2 §4) and which shot asks for which.
 *
 * One per line of the spec's table, keyed by the SHOT FAMILY the seeded scripts use (`help/helpCopy.ts`), plus the
 * orientation cue, which belongs to no family: "turn your phone sideways" is demonstrated for the first shot of
 * the case that asks for LANDSCAPE, whatever it is.
 *
 * An unknown prompt key gets NO demonstration — never an invented one (the same rule `helpCopy` states for the
 * "why this shot" line). The captions live in the decks; nothing is baked into the art.
 */

import type { ScriptShot } from '@/api/capture';
import { familyOf } from '@/flow/help/helpCopy';

export const DEMO_TYPES = ['STREET', 'SIDEWAYS', 'DOOR', 'TURN', 'PLATE', 'CERTIFICATES', 'FACE', 'ID_FLAT'] as const;
export type DemoType = (typeof DEMO_TYPES)[number];

/** `sv.showme.<type>.cap` — the §4 caption, a verb phrase, the ONE caption source for art, sheet and speech. */
export function demoCaptionKey(type: DemoType): string {
  return `sv.showme.${type}.cap`;
}

/** The demonstration for the shot itself (its family), or null when the prompt key is one we do not teach. */
export function familyDemoFor(promptKey: string | null | undefined): DemoType | null {
  switch (familyOf(promptKey)) {
    case 'EXT':
    case 'HOME':
      return 'STREET';
    case 'ENTRANCE':
      return 'DOOR';
    case 'INT':
    case 'ACTIVITY':
      return 'TURN';
    case 'DOOR_PLATE':
    case 'INSTALL_POINT':
      return 'PLATE';
    case 'CERT_WALL':
      return 'CERTIFICATES';
    case 'REP_SELFIE':
      return 'FACE';
    case 'ID_DOCUMENT':
      return 'ID_FLAT';
    default:
      return null;
  }
}

/**
 * Every demonstration this shot could teach, in the order they help: hold the phone right first, then frame the
 * subject. Used by the auto-play (first unseen wins) and by the help sheet (the family one is the preview).
 */
export function demosFor(shot: Pick<ScriptShot, 'promptKey' | 'orientation'>): readonly DemoType[] {
  const out: DemoType[] = [];
  if (shot.orientation === 'LANDSCAPE') out.push('SIDEWAYS');
  const family = familyDemoFor(shot.promptKey);
  if (family) out.push(family);
  return out;
}
